import React, { useEffect, useState, useRef } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DashboardHelpInfoTab from './Dashboard-HelpInfo-Tab';
import DashboardExportTab from './Dashboard-Export-Tab';
import SwitchProducts from './SwitchProducts'; // Reintroduce SwitchProducts component
import envConfig from '../envConfig';
import { MultiSelect } from "react-multi-select-component";
import Product4Map from './Product4Map';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { useSelectedProduct } from '../contexts/SelectedProductContext'; // Assumed context

function Dashboard(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedProduct, setSelectedProduct, isFromUI, setIsFromUI } = useSelectedProduct(); // Context for product selection
    const [selectedDataView, setSelectedDataView] = useState('hazard');
    const [vulnerabilityPurpose, setVulnerabilityPurpose] = useState('indicator');
    const [selectedSector, setSelectedSector] = useState("");
    const [selectedTimeScale, setSelectedTimeScale] = useState("");
    const [vulnerabilityIndicator, setVulnerabilityIndicator] = useState("");
    const [selectedVulnerability, setSelectedVulnerability] = useState(null);
    const [expandedVulnerabilities, setExpandedVulnerabilities] = useState({});
    const hasRun = useRef(false);

    const [isOpen, setIsOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(1);
    const [currentTab, setCurrentTab] = useState('layer'); // Added for navbar tab
    const [show, setShow] = useState(false); // State for SwitchProducts modal
    const [height, setHeight] = useState(window.innerHeight);
    const [countries, setCountries] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [timeScales, setTimesScales] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [filteredIndicators, setFilteredIndicators] = useState([]);
    const [loading, setLoading] = useState(0);
    const [lookUpLoading, setLookUpLoading] = useState(0);
    const [isApplied, setIsApplied] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedDataMap, setSelectedDataMap] = useState(null);

    // Sync product selection with URL
    useEffect(() => {
        const path = location.pathname;
        const productMatch = path.match(/^\/(product[1-4])\/dashboard$/);
        const newProduct = productMatch ? productMatch[1] : 'product4';

        if (newProduct !== selectedProduct && !isFromUI) {
            console.log(`URL Sync: Setting product to ${newProduct} from URL ${path}`);
            setSelectedProduct(newProduct);
            setActiveTab(1);
            setCurrentTab('layer');
        }
    }, [location.pathname, selectedProduct, setSelectedProduct, isFromUI, setIsFromUI]);

    // Handle product selection
    const handleProductSelect = (product) => {
        console.log(`Dropdown: Changing product to ${product}`);
        setIsFromUI(true);
        setSelectedProduct(product);
        setActiveTab(1);
        setCurrentTab('layer');
        navigate(`/${product}/dashboard`, { replace: false });
        setTimeout(() => setIsFromUI(false), 0);
    };

    const dataViewChange = (event) => {
        setSelectedDataView(event.target.value);
    };

    const changeVulnerabilityPurpose = (event) => {
        setVulnerabilityPurpose(event.target.value);
    };

    const changeVulnerabilityIndicator = (item) => {
        setVulnerabilityIndicator(item);
    };

    const changeTimeScale = (event) => {
        setSelectedTimeScale(+event.target.value);
    };

    const toggleSidenav = () => {
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleShow = () => setShow(true);

    const handleTabClick = (currentTab) => {
        if (loading) return;
        if (currentTab !== activeTab) {
            setActiveTab(currentTab);
            if (currentTab === 4 || currentTab === 5) {
                setCurrentTab(null); // Reset currentTab for Help/Info or Export/Download
            } else {
                setCurrentTab('layer');
            }
        }
    };

    const handleResize = () => {
        setHeight(window.innerHeight);
    };

    // Toggle expand/collapse for a vulnerability category
    const toggleVulnerability = (vulnerability) => {
        setExpandedVulnerabilities((prev) => ({
            ...prev,
            [vulnerability]: !prev[vulnerability],
        }));
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (sectors.length > 0) {
            handleSectorChange({ target: { value: sectors[0].sector_id } });
        }
    }, [sectors]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/adm0`);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const result = await response.json();
                return result.data;
            } catch (error) {
                console.error('Failed to fetch countries:', error);
            }
        };

        const fetchSectors = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/riskdrought/sectors`);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const result = await response.json();
                return result.data;
            } catch (error) {
                console.error('Failed to fetch GeoJSON:', error);
                throw error;
            }
        };

        const fetchTimeScales = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/riskdrought/timescales`);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const result = await response.json();
                return result.data;
            } catch (error) {
                console.error('Failed to fetch GeoJSON:', error);
                throw error;
            }
        };

        const fetchVulnerabilyIndicators = async () => {
            let payload = {
                "sector_ids": [],
                "vulnerability_ids": []
            };
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/riskdrought/vulnerability_indicators`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const result = await response.json();
                return result.data;
            } catch (error) {
                console.error('Failed to fetch GeoJSON:', error);
                throw error;
            }
        };

        const fetchData = async () => {
            try {
                setLookUpLoading(true);
                const [countries, sectors, timeScales, indicators] = await Promise.all([
                    fetchCountries(),
                    fetchSectors(),
                    fetchTimeScales(),
                    fetchVulnerabilyIndicators()
                ]);
                setCountries(countries || []);
                setSectors(sectors || []);
                setTimesScales(timeScales || []);
                setIndicators(indicators || []);
                const allCountries = countries.map((country) => ({
                    value: country.adm0_code,
                    label: country.adm0_name,
                }));
                setSelectedCountries(allCountries);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLookUpLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (selectedCountries.length > 0 && selectedTimeScale && !hasRun.current) {
            handleApply();
            hasRun.current = true;
        }
    }, [selectedCountries, selectedTimeScale]);

    const handleApply = () => {
        setSelectedDataMap(null);
        setIsApplied(prev => !prev);
        getRiskDroughtData();
    };

    const getRiskDroughtData = async () => {
        let payload;
        let url = '';
        let countryIds = selectedCountries.map(item => item.value);
        if (selectedDataView === 'vulnerability') {
            url = `/riskdrought/vulnerability`;
            if (vulnerabilityPurpose == 'ranking') {
                payload = {
                    "purpose": "rank",
                    "adm0_code": countryIds,
                    "sector_id": selectedSector,
                    "vulnerability_id": selectedVulnerability,
                    "geometry_required": true
                };
            } else if (vulnerabilityPurpose == 'indicator') {
                if (!vulnerabilityIndicator) {
                    alert('please select a vulnerability indicator');
                    return;
                }
                payload = {
                    "purpose": "indicator",
                    "adm0_code": countryIds,
                    "indicator_id": vulnerabilityIndicator.indicator_id,
                    "geometry_required": true
                };
            }
        } else if (selectedDataView === 'hazard') {
            url = `/riskdrought/hazard`;
            if (!selectedTimeScale) {
                alert('please select a time frame');
                return;
            }
            payload = {
                "adm0_code": countryIds,
                "timescale_id": selectedTimeScale
            };
        } else if (selectedDataView === 'risk') {
            url = `/riskdrought/risk`;
            if (!selectedTimeScale) {
                alert('please select a time frame');
                return;
            }
            payload = {
                "adm0_code": countryIds,
                "sector_id": selectedSector,
                "timescale_id": selectedTimeScale
            };
        }
        try {
            setLoading(prev => prev + 1);
            const response = await fetch(`${envConfig.API_URL}${url}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }
            const result = await response.json();
            setSelectedDataMap(result.data);
        } catch (error) {
            console.error("Failed to send hazard:", error);
        } finally {
            setLoading(prev => (prev > 0 ? prev - 1 : 0));
        }
    };

    const LookUpLoader = () => (
        <div>
            <div className="spinner-border text-primary" role="status" style={{ width: '1rem', height: '1rem' }}>
                <span className="sr-only"></span>
            </div>
        </div>
    );

    const handleCountrySelect = async (selectedList) => {
        const countryIds = selectedList.map(item => item.value);
        if (selectedList) {
            setSelectedCountries(selectedList);
        }
    };

    const customValueRenderer = (selected, _options) => {
        if (selected.length === 0) {
            return "Select Countries";
        }
        if (selected.length === countries.length && countries.length > 0) {
            return "All countries are selected";
        }
        return `${selected.length} country${selected.length > 1 ? 's' : ''} selected`;
    };

    useEffect(() => {
        if (timeScales.length > 0) {
            setSelectedTimeScale(timeScales[0].timescale_id);
        }
    }, [timeScales]);

    useEffect(() => {
        if (filteredIndicators.length > 0) {
            const groupedIndicators = Object.values(
                filteredIndicators.reduce((acc, indicator) => {
                    if (!acc[indicator.vulnerability]) {
                        acc[indicator.vulnerability] = [];
                    }
                    acc[indicator.vulnerability].push(indicator);
                    return acc;
                }, {})
            );
            const firstItem = groupedIndicators[0]?.[0];
            if (firstItem) {
                changeVulnerabilityIndicator(firstItem);
            }
        }
    }, [filteredIndicators]);

    const getFilteredIndicators = (sectorId) => {
        let data = indicators.filter(item => item.sector_id == sectorId);
        setFilteredIndicators(data);
    };

    const handleSectorChange = (event) => {
        const sectorId = event.target.value;
        setSelectedSector(sectorId);
        getFilteredIndicators(sectorId);
    };

    return (
        <>
            <nav className="navbar navbar-expand-lg navbar-light bg-light fixed-top p-1">
                <div className="container-fluid px-0">
                    <Link className="navbar-brand" to="/">
                        <img loading="lazy" src="/assets/images/logo.svg" alt="Logo" />
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#navbarText"
                        aria-controls="navbarText"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse pl-52px d-flex justify-content-between align-items-center" id="navbarText">
                        <div className="d-flex justify-content-center flex-grow-1">
                            <div className="d-flex align-items-center">
                                <h5 style={{ margin: "0" }}>{({
                                    'product1': 'Multi Hazard Mapping',
                                    'product2': 'Vulnerability and Risk Analysis',
                                    'product3': 'Flood Forecasting and Early Warning',
                                    'product4': 'Flood and Drought Monitor'
                                })[selectedProduct] || 'Flood and Drought Monitor'}</h5>
                                <Link
                                    to="#"
                                    onClick={() => { handleShow(); }}
                                    className={activeTab === 2 ? 'btn btn-contact me-1' : 'nav-link ms-3'}
                                    style={activeTab === 2 ? { width: '60px' } : {}}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Switch Product"
                                >
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M3 7H19M19 7L15 3M19 7L15 11M19 15H3M3 15L7 11M3 15L7 19"
                                            stroke={activeTab === 2 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                        <ul className="navbar-nav d-flex flex-row align-items-center">
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 1 ? 'btn btn-contact me-1' : `nav-link${loading ? ' disabled' : ''}`}
                                    style={activeTab === 1 ? { width: '60px' } : {}}
                                    onClick={() => setActiveTab(1)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Layers"
                                >
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M1.8335 15.5833L11.0002 20.1667L20.1668 15.5833M1.8335 11L11.0002 15.5833L20.1668 11M11.0002 1.83334L1.8335 6.41668L11.0002 11L20.1668 6.41668L11.0002 1.83334Z"
                                            stroke={activeTab === 1 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 4 ? 'btn btn-contact me-1' : 'nav-link'}
                                    style={activeTab === 4 ? { width: '60px' } : {}}
                                    onClick={() => !loading && setActiveTab(4)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Help/Info"
                                >
                                    <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M8.83266 8.25001C9.04817 7.63737 9.47355 7.12077 10.0335 6.79172C10.5934 6.46266 11.2517 6.34237 11.8917 6.45216C12.5318 6.56196 13.1124 6.89474 13.5306 7.39158C13.9489 7.88841 14.1778 8.51724 14.1768 9.16668C14.1768 11 11.4268 11.9167 11.4268 11.9167M11.5002 15.5833H11.5093M20.6668 11C20.6668 16.0626 16.5628 20.1667 11.5002 20.1667C6.43755 20.1667 2.3335 16.0626 2.3335 11C2.3335 5.9374 6.43755 1.83334 11.5002 1.83334C16.5628 1.83334 20.6668 5.9374 20.6668 11Z"
                                            stroke={activeTab === 4 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 5 ? 'btn btn-contact me-1' : `nav-link${loading ? ' disabled' : ''}`}
                                    style={activeTab === 5 ? { width: '60px' } : {}}
                                    onClick={() => !loading && setActiveTab(5)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Export/Download"
                                >
                                    <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M19.75 13.75V17.4167C19.75 17.9029 19.5568 18.3692 19.213 18.713C18.8692 19.0568 18.4029 19.25 17.9167 19.25H5.08333C4.5971 19.25 4.13079 19.0568 3.78697 18.713C3.44315 18.3692 3.25 17.9029 3.25 17.4167V13.75M6.91667 9.16667L11.5 13.75M11.5 13.75L16.0833 9.16667M11.5 13.75V2.75"
                                            stroke={activeTab === 5 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    className="btn btn-contact me-1"
                                    to="/"
                                    style={{ width: '60px' }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Home"
                                >
                                    <i className="bi bi-house fs-6"></i>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="tab-content" id="myTabContent" style={{ height: '100%' }}>
                <div
                    className="help-info-container"
                    style={{ display: activeTab === 4 ? 'block' : 'none' }}
                >
                    <DashboardHelpInfoTab key="help-info" />
                </div>
                <div
                    className="export-download-container"
                    style={{ display: activeTab === 5 ? 'block' : 'none' }}
                >
                    <DashboardExportTab key="export-download" />
                </div>
                <div
                    className={`tab-pane ${currentTab === 'layer' && activeTab === 1 ? 'show active' : ''}`}
                    id="layer"
                    role="tabpanel"
                    aria-labelledby="layer-tab"
                    style={{ display: activeTab === 1 && currentTab === 'layer' ? 'block' : 'none' }}
                >
                    <div className="container-fluid d-flex px-0">
                        <div className={`sidenav p5 ${isOpen ? 'open' : 'collapsed'}`}>
                            <button className="toggle-btn newBtn" onClick={toggleSidenav}>
                                <img
                                    src={isOpen ? '../assets/images/Layout.png' : '../assets/images/LayoutWhite.svg'}
                                    alt="Toggle Sidenav"
                                    className="toggle-icon"
                                />
                            </button>
                            {isOpen ? (
                                <div className="card border-0" style={{ marginTop: '-8px', height: '600px', overflow: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}>
                                    <div className="card-body p-0">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12 px-1">
                                                <div className="accordian-filter">
                                                    <Accordion defaultActiveKey={['0']} alwaysOpen>
                                                        <Accordion.Item eventKey="0">
                                                            <Accordion.Body className="pt-0 p-2">
                                                                <div className="row">
                                                                    <div className="col-sm-12 col-md-12 col-lg-12">
                                                                        <div className="form-group">
                                                                            <label>Country</label>
                                                                            {lookUpLoading ? (
                                                                                <LookUpLoader />
                                                                            ) : (
                                                                                <MultiSelect
                                                                                    id="countrySelect"
                                                                                    className="custom-multiselect"
                                                                                    options={countries.map((country) => ({
                                                                                        value: country.adm0_code,
                                                                                        label: country.adm0_name,
                                                                                    }))}
                                                                                    value={selectedCountries}
                                                                                    onChange={handleCountrySelect}
                                                                                    labelledBy="Select"
                                                                                    overrideStrings={{
                                                                                        selectSomeItems: "Select Countries",
                                                                                        allItemsAreSelected: "All countries are selected",
                                                                                        selectAll: "Select All",
                                                                                        search: "Search",
                                                                                    }}
                                                                                    valueRenderer={customValueRenderer}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div className="form-group mt-2">
                                                                            <label>Sector</label>
                                                                            {lookUpLoading ? (
                                                                                <LookUpLoader />
                                                                            ) : (
                                                                                <select className="form-select mt-2" onChange={handleSectorChange}>
                                                                                    {sectors.map((sector) => (
                                                                                        <option key={sector.sector_id} value={sector.sector_id}>
                                                                                            {sector.sector}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            )}
                                                                        </div>
                                                                        <div className="form-group mt-2">
                                                                            <label>Data View (Hazard X Vulnerability = Risk)</label>
                                                                            <br />
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="inlineRadioOptions"
                                                                                    id="inlineRadio1"
                                                                                    value="hazard"
                                                                                    onChange={dataViewChange}
                                                                                    checked={selectedDataView === "hazard"}
                                                                                />
                                                                                <label className="form-check-label" htmlFor="inlineRadio1">Hazard</label>
                                                                            </div>
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="inlineRadioOptions"
                                                                                    id="inlineRadio2"
                                                                                    value="vulnerability"
                                                                                    onChange={dataViewChange}
                                                                                    checked={selectedDataView === "vulnerability"}
                                                                                />
                                                                                <label className="form-check-label" htmlFor="inlineRadio2">Vulnerability</label>
                                                                            </div>
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="inlineRadioOptions"
                                                                                    id="inlineRadio3"
                                                                                    value="risk"
                                                                                    onChange={dataViewChange}
                                                                                    checked={selectedDataView === "risk"}
                                                                                />
                                                                                <label className="form-check-label" htmlFor="inlineRadio3">Risk</label>
                                                                            </div>
                                                                            {selectedDataView === "vulnerability" && (
                                                                                <div className="mt-2">
                                                                                    <label className="">Indicators</label>
                                                                                    <br />
                                                                                    <div className="form-check form-check-inline">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="radio"
                                                                                            name="vulnerabilityOptions"
                                                                                            id="inlineRadio5"
                                                                                            value="indicator"
                                                                                            onChange={changeVulnerabilityPurpose}
                                                                                            checked={vulnerabilityPurpose === "indicator"}
                                                                                        />
                                                                                        <label className="form-check-label" htmlFor="inlineRadio5">Indicator</label>
                                                                                    </div>
                                                                                    <div className="form-check form-check-inline">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="radio"
                                                                                            name="vulnerabilityOptions"
                                                                                            id="inlineRadio4"
                                                                                            value="ranking"
                                                                                            onChange={changeVulnerabilityPurpose}
                                                                                            checked={vulnerabilityPurpose === "ranking"}
                                                                                        />
                                                                                        <label className="form-check-label" htmlFor="inlineRadio4">Ranking</label>
                                                                                    </div>
                                                                                    {vulnerabilityPurpose === "ranking" && (
                                                                                        <>
                                                                                            <div className="form-check">
                                                                                                <input
                                                                                                    className="form-check-input"
                                                                                                    type="radio"
                                                                                                    name="vulnerabilitySelection"
                                                                                                    id="radio-null"
                                                                                                    value="null"
                                                                                                    checked={selectedVulnerability === null}
                                                                                                    onChange={() => setSelectedVulnerability(null)}
                                                                                                />
                                                                                                <label className="form-check-label" htmlFor="radio-null">
                                                                                                    Cumulative Rank
                                                                                                </label>
                                                                                            </div>
                                                                                            {Array.from(new Map(filteredIndicators.map(item => [item.vulnerability, item])).values()).map((item, index) => (
                                                                                                <div key={item.vulnerability_id} className="form-check">
                                                                                                    <input
                                                                                                        className="form-check-input"
                                                                                                        type="radio"
                                                                                                        name="vulnerabilitySelection"
                                                                                                        id={`radio-${index}`}
                                                                                                        value={item.vulnerability_id}
                                                                                                        checked={selectedVulnerability === item.vulnerability_id}
                                                                                                        onChange={() => setSelectedVulnerability(item.vulnerability_id)}
                                                                                                    />
                                                                                                    <label className="form-check-label" htmlFor={`radio-${index}`}>
                                                                                                        {item.vulnerability}
                                                                                                    </label>
                                                                                                </div>
                                                                                            ))}
                                                                                        </>
                                                                                    )}
                                                                                    {vulnerabilityPurpose === "indicator" &&
                                                                                        Object.entries(
                                                                                            filteredIndicators.reduce((acc, indicator) => {
                                                                                                if (!acc[indicator.vulnerability]) {
                                                                                                    acc[indicator.vulnerability] = [];
                                                                                                }
                                                                                                acc[indicator.vulnerability].push(indicator);
                                                                                                return acc;
                                                                                            }, {})
                                                                                        ).map(([vulnerability, items]) => (
                                                                                            <div key={vulnerability} className='mt-2'>
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <label className="labelVu">
                                                                                                        {vulnerability}
                                                                                                    </label>
                                                                                                    <button
                                                                                                        className="btn btn-link p-0 ml-2"
                                                                                                        onClick={() => toggleVulnerability(vulnerability)}
                                                                                                        style={{ lineHeight: '1' }}
                                                                                                    >
                                                                                                        <svg
                                                                                                            width="16"
                                                                                                            height="16"
                                                                                                            viewBox="0 0 16 16"
                                                                                                            fill="none"
                                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                                        >
                                                                                                            {expandedVulnerabilities[vulnerability] ? (
                                                                                                                <path
                                                                                                                    d="M12 10L8 6L4 10"
                                                                                                                    stroke="black"
                                                                                                                    strokeWidth="2"
                                                                                                                    strokeLinecap="round"
                                                                                                                    strokeLinejoin="round"
                                                                                                                />
                                                                                                            ) : (
                                                                                                                <path
                                                                                                                    d="M4 6L8 10L12 6"
                                                                                                                    stroke="black"
                                                                                                                    strokeWidth="2"
                                                                                                                    strokeLinecap="round"
                                                                                                                    strokeLinejoin="round"
                                                                                                                />
                                                                                                            )}
                                                                                                        </svg>
                                                                                                    </button>
                                                                                                </div>
                                                                                                {expandedVulnerabilities[vulnerability] && (
                                                                                                    <>
                                                                                                        {items.map((item, index) => (
                                                                                                            <div key={item.indicator_id} className="form-check">
                                                                                                                <input
                                                                                                                    className="form-check-input"
                                                                                                                    type="radio"
                                                                                                                    name="indicatorSelection"
                                                                                                                    id={`radio-${item.indicator_id}`}
                                                                                                                    value={item.field}
                                                                                                                    checked={vulnerabilityIndicator?.indicator_id === item.indicator_id}
                                                                                                                    onChange={() => changeVulnerabilityIndicator(item)}
                                                                                                                />
                                                                                                                <label
                                                                                                                    className="form-check-label"
                                                                                                                    htmlFor={`radio-${item.indicator_id}`}
                                                                                                                >
                                                                                                                    {item.label}
                                                                                                                </label>
                                                                                                            </div>
                                                                                                        ))}
                                                                                                    </>
                                                                                                )}
                                                                                            </div>
                                                                                        ))
                                                                                    }
                                                                                </div>
                                                                            )}
                                                                            {(selectedDataView === 'risk' || selectedDataView === 'hazard') && (
                                                                                <div className="mt-2">
                                                                                    <label>Time Frame</label>
                                                                                    <br />
                                                                                    {timeScales.map((timeScale) => (
                                                                                        <div key={timeScale.timescale_id} className="form-check">
                                                                                            <input
                                                                                                className="form-check-input"
                                                                                                type="radio"
                                                                                                name="timeScaleRadios"
                                                                                                id={`timeScale-${timeScale.timescale_id}`}
                                                                                                value={timeScale.timescale_id}
                                                                                                checked={selectedTimeScale === timeScale.timescale_id}
                                                                                                onChange={changeTimeScale}
                                                                                            />
                                                                                            <label
                                                                                                className="form-check-label"
                                                                                                htmlFor={`timeScale-${timeScale.timescale_id}`}
                                                                                            >
                                                                                                {timeScale.timescale}
                                                                                            </label>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <button type="button" className="btn btn-apply w-100 mt-2" onClick={handleApply}>
                                                                            {loading ? 'Wait...' : 'Apply'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <img src="../assets/images/filters.svg" alt="Collapsed Icon" className="collapsed-icon" />
                            )}
                        </div>
                        <Product4Map selectedDataMap={selectedDataMap} />
                    </div>
                </div>
            </div>

            <div className="App">
                <SwitchProducts show={show} handleClose={handleClose} />
            </div>
        </>
    );
}

export default Dashboard;